import {GeoData} from "@/types/GeoData";
import { Route } from "@/types/routes";
import {Device, DeviceType} from "@/types/devices";


export class ApiClient {
    private baseUrl: string;
    private token: string | null = null;
    private login: string;
    private password: string;

    constructor() {
        const rootDiv = document.getElementById("root");
        if (!rootDiv) throw new Error("Root div не найден");

        this.baseUrl = (process.env.REACT_APP_LOCAL === 'true') ? "http://localhost:8888" : (rootDiv.dataset.server || "");
        this.login = rootDiv.dataset.login || "";
        this.password = rootDiv.dataset.password || "";

        if (!this.baseUrl || !this.login || !this.password) {
            throw new Error("Реквизиты для API не найдены в div#root");
        }
    }

    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        if (!this.token && !(await this.authenticate())) {
            throw new Error("Ошибка авторизации. Данные недоступны.");
        }

        const headers = new Headers(options.headers);
        headers.set("Authorization", `${this.token}`);

        const response = await fetch(`${this.baseUrl}${endpoint}`, {
            ...options,
            headers,
        });

        if (response.status === 401) {
            if (await this.authenticate()) {
                return this.request<T>(endpoint, options);
            } else {
                throw new Error("Ошибка доступа: 401. Повторная авторизация не удалась.");
            }
        }

        if (!response.ok) throw new Error(`Ошибка запроса: ${response.statusText}`);

        return response.json();
    }

    async authenticate(): Promise<boolean> {
        try {
            const response = await fetch(`${this.baseUrl}/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ login: this.login, password: this.password }),
            });

            if (response.status === 401) {
                alert("Неверный логин или пароль!");
                return false;
            }

            if (!response.ok) throw new Error("Ошибка логина");

            const data = await response.json();
            this.token = data.token;
            return true;
        } catch (error) {
            console.error("Ошибка авторизации:", error);
            return false;
        }
    }

    async getDeviceTypes(): Promise<DeviceType[]> {
        return this.request<DeviceType[]>("/devices/types");
    }

    async updateDeviceTypeName(id: string, name: string): Promise<void> {
        return this.request<void>("/devices/type/name", {
            method: "POST",
            body: JSON.stringify({ id, name }),
            headers: { "Content-Type": "application/json" },
        });
    }

    async togglePublicAccess(id: string, enable: boolean): Promise<void> {
        return this.request<void>(`/devices/type/public/${enable ? "enable" : "disable"}`, {
            method: "POST",
            body: JSON.stringify({ id }),
            headers: { "Content-Type": "application/json" },
        });
    }

    async getDevices(): Promise<Device[]> {
        return this.request<Device[]>("/devices");
    }

    async updateDeviceName(id: string, name: string): Promise<void> {
        return this.request<void>("/devices/name", {
            method: "POST",
            body: JSON.stringify({ id, name }),
            headers: { "Content-Type": "application/json" },
        });
    }

    async updateDeviceIcon(id: string, icon: string): Promise<void> {
        return this.request<void>("/devices/icon", {
            method: "POST",
            body: JSON.stringify({ id, icon }),
            headers: { "Content-Type": "application/json" },
        });
    }

    async updateDeviceColor(id: string, color: string): Promise<void> {
        return this.request<void>("/devices/color", {
            method: "POST",
            body: JSON.stringify({ id, color }),
            headers: { "Content-Type": "application/json" },
        });
    }

    async toggleDevice(id: string, enable: boolean): Promise<void> {
        return this.request<void>(`/devices/${enable ? "enable" : "disable"}`, {
            method: "POST",
            body: JSON.stringify({ id }),
            headers: { "Content-Type": "application/json" },
        });
    }
    async connectRealtime(
        types: string[],
        devices: string[],
        onPointReceived: (point: GeoData) => void,
        onError: (error: string) => void
    ) {
        if (!this.token && !(await this.authenticate())) {
            throw new Error("Ошибка авторизации. Данные недоступны.");
        }
        const ws = new WebSocket(`${this.baseUrl.replace("https", "wss")}/ws/real`);
        ws.onopen = () => {
            ws.send(JSON.stringify({ types, devices, token: this.token }));
        };
        ws.onmessage = (event) => {
            try {
                const data: GeoData = JSON.parse(event.data);
                if (data) {
                    onPointReceived(data);
                } else {
                    throw new Error(event.data);
                }
            } catch (error) {
                onError(event.data);
            }
        };
        ws.onerror = (error) => {
            console.error("Ошибка WebSocket:", error);
        };
        return ws;
    }

    async connectHistory(
        from: number,
        to: number,
        speed: number,
        types: string[],
        devices: string[],
        onPointReceived: (point: GeoData) => void,
        onError: (error: string) => void,
        onComplete: () => void
    ) {
        if (!this.token && !(await this.authenticate())) {
            throw new Error("Ошибка авторизации. Данные недоступны.");
        }
        const ws = new WebSocket(`${this.baseUrl.replace("https", "wss")}/ws/replay`);
        ws.onopen = () => {
            ws.send(JSON.stringify({ from, to, speed, types, devices, token: this.token }));
        };
        ws.onmessage = (event) => {
            try {
                const data: GeoData = JSON.parse(event.data);
                if (data) {
                    onPointReceived(data);
                } else if (event.data === "replay complete") {
                    onComplete();
                } else {
                    throw new Error(event.data);
                }
            } catch (error) {
                if (event.data === "replay complete") {
                    onComplete();
                } else {
                    onError(event.data);
                }
            }
        };
        ws.onerror = (error) => {
            console.error("Ошибка WebSocket:", error);
        };
        return ws;
    }

    async getLastDevicePoints(types: string[]): Promise<GeoData[]> {
        const oneWeekAgo = Math.floor(Date.now() / 1000) - 2 * 60 * 60; // Текущая дата минус 2 часа
        const now = Math.floor(Date.now() / 1000);

        return this.request<GeoData[]>("/devices/last", {
            method: "POST",
            body: JSON.stringify({
                date_from: oneWeekAgo,
                date_to: now,
                types,
                devices: [],
            }),
            headers: { "Content-Type": "application/json" },
        });
    }


    async getAllRoutes(): Promise<Route[]> {
        return this.request<Route[]>("/routes/all");
    }

    async createRoute(data: string): Promise<Route | string> {
        return this.request<Route | string>("/routes/create", {
            method: "POST",
            body: JSON.stringify({ data }),
            headers: { "Content-Type": "application/json" },
        });
    }

    async updateRoute(route: { id: string; name: string; active: boolean; color: string; }): Promise<Route | string> {
        return this.request<Route | string>("/routes/update", {
            method: "POST",
            body: JSON.stringify(route),
            headers: { "Content-Type": "application/json" },
        });
    }

    async importRoute(id: string, data: string): Promise<Route | string> {
        return this.request<Route | string>("/routes/import", {
            method: "POST",
            body: JSON.stringify({ id, data }),
            headers: { "Content-Type": "application/json" },
        });
    }

    async getActiveRoutes(): Promise<Route[]> {
        return this.request<Route[]>("/routes/active");
    }

    async setDeviceRoute(id: string, route_id: string): Promise<void> {
        await this.request<void>("/devices/route", {
            method: "POST",
            body: JSON.stringify({ id, route_id }),
            headers: { "Content-Type": "application/json" },
        });
    }

}

export const apiClient = new ApiClient();
